import { gql } from "@apollo/client";

import { MOBLYZE_JOB_FRAGMENT } from "./jobs";

// A job's owner (JobOwner { id firstName lastName email role }) is the person
// it belongs to. They get the job's Mya thread and are shown to hired
// candidates as the agency contact. Its fields are spelled out in each
// document rather than shared as a fragment, so mocked responses need no
// __typename.

// The people the current user can make a job's owner: a recruiter's agency
// colleagues, or a client's organisation members.
export const JOB_OWNER_OPTIONS_QUERY = gql`
  query JobOwnerOptions {
    jobOwnerOptions {
      id
      firstName
      lastName
      email
      role
    }
  }
`;

export const JOB_OWNER_QUERY = gql`
  query JobOwner($id: ID!) {
    moblyzeJob(id: $id) {
      id
      category
      owner {
        id
        firstName
        lastName
        email
        role
      }
    }
  }
`;

// Reassigns a job's owner and nothing else. Use it wherever only the owner
// changes: unlike updateJobDetails it leaves the job's version alone, so
// matching does not re-run on a published job. Where the whole form is saved
// anyway, ownerUserId goes in updateJobDetails instead.
export const SET_JOB_OWNER_MUTATION = gql`
  mutation SetJobOwner($moblyzeJobId: ID!, $ownerUserId: ID!) {
    setJobOwner(moblyzeJobId: $moblyzeJobId, ownerUserId: $ownerUserId) {
      id
      owner {
        id
        firstName
        lastName
        email
        role
      }
    }
  }
`;

export const BULLHORN_JOB_FRAGMENT = gql`
  fragment bullhornJobFragment on BullhornJob {
    id
    bullhornId
    jobTitle
    client
    employmentType
    openClosed
    status
    numOpenings
    dateAdded
    vertical
    address
    city
    county
    postcode
    country
    contact
    jobInformation
    locationRequirements
    payRate
    payUnit
    salaryHigh
    salaryLow
    billRate
    industries
    industriesCount
    jobCategory
    jobCategoryCount
    jobDuration
    publishedDescription
    region
    requiredSkills
    startDate
    worksite
    currency

    moblyzeJobShell {
      bullhornId

      category
      title
      employmentType
      numOpenings

      employerContactName
      employerContactEmail
      employerContactPhone

      agencyContactName
      agencyContactEmail
      agencyContactPhone

      location

      duration
      startDate

      requirementsDescription
      responsibilitiesDescription
      otherDescription

      vesselId

      hiredInstructions
      siteAddress
    }
  }
`;

export const JOB_CREATE_PAGE_QUERY = gql`
  {
    getClientOrganizations {
      id
      name
    }

    candidateRoles {
      id
      name
      requiredSkills {
        id
        name
      }
      requiredCertifications {
        id
        name
      }
    }

    candidateWorkLocations {
      id
      name
    }

    getVessels {
      id
      name
      subtitle
      flag
      ageDescription
      wifiSpeedDescription
      isSmoking
      totalCrew
      vesselUrl
      imageUrl
    }
  }
`;

export const JOB_EDIT_PAGE_QUERY = gql`
  ${MOBLYZE_JOB_FRAGMENT}
  query JobEditPageQuery($id: ID!) {
    moblyzeJob(id: $id) {
      ...MoblyzeJobFragment
      owner {
        id
        firstName
        lastName
        email
        role
      }
    }

    getClientOrganizations {
      id
      name
    }

    getSkills(moblyzeJobId: $id) {
      id
      name
    }

    getCertificationsByMoblyzeJobId(moblyzeJobId: $id) {
      id
      name
    }

    candidateRoles {
      id
      name
      requiredSkills {
        id
        name
      }
      requiredCertifications {
        id
        name
      }
    }

    candidateWorkLocations {
      id
      name
    }

    getVessels {
      id
      name
      subtitle
      flag
      ageDescription
      wifiSpeedDescription
      isSmoking
      totalCrew
      vesselUrl
      imageUrl
    }
  }
`;

export const GET_SKILLS_BY_ROLE_QUERY = gql`
  query GetSkills($roleId: [ID!]!) {
    getSkillsByCandidateRoleID(candidateRoleID: $roleId) {
      id
      name
    }
  }
`;

export const GET_CERTIFICATIONS_BY_ROLE_QUERY = gql`
  query GetCertifications($roleId: [ID!]!) {
    getCertificationsByCandidateRoleID(candidateRoleID: $roleId) {
      id
      name
    }
  }
`;

export const UNCURATED_JOBS_QUERY = gql`
  ${BULLHORN_JOB_FRAGMENT}
  query UncuratedJobsQuery {
    agencyUncuratedJobs {
      ...bullhornJobFragment
    }
  }
`;

export const CURATE_JOB_PAGE_QUERY = gql`
  ${BULLHORN_JOB_FRAGMENT}
  query UncuratedJobPageQuery($bullhornJobId: ID!) {
    bullhornJob(bullhornJobId: $bullhornJobId) {
      ...bullhornJobFragment
    }

    getClientOrganizations {
      id
      name
    }

    candidateRoles {
      id
      name
    }

    candidateWorkLocations {
      id
      name
    }

    getVessels {
      id
      name
      subtitle
      flag
      ageDescription
      wifiSpeedDescription
      isSmoking
      totalCrew
      vesselUrl
      imageUrl
    }
  }
`;

export const JOB_POSTING_SOURCE_CONTEXT_QUERY = gql`
  query JobPostingSourceContext($bullhornId: String!) {
    jobPostingSourceContext(bullhornId: $bullhornId) {
      prefillText
      bullhornJobOrderId
      clientOrganizationHint {
        clientOrganizationId
        clientCorporationName
        matchConfidence
      }
    }
  }
`;

export const GENERATE_JOB_LISTING_DRAFT_MUTATION = gql`
  mutation GenerateJobListingDraft($input: GenerateJobListingDraftInput!) {
    generateJobListingDraft(input: $input) {
      generationRunId
      draft {
        bullhornId
        title
        category
        employmentType
        numOpenings
        clientOrganizationId
        employerContactName
        employerContactEmail
        employerContactPhone
        agencyContactName
        agencyContactEmail
        agencyContactPhone
        payRateValue
        payFrequency
        payCurrency
        location
        duration
        startDate
        isAsap
        requirementsDescription
        responsibilitiesDescription
        otherDescription
        candidateWorkLocationIds
        vesselId
        coverImageIndex
      }
    }
  }
`;

export const CREATE_JOB_MUTATION = gql`
  mutation CreateJob(
    $bullhornId: String
    $category: String
    $title: String
    $employmentType: MoblyzeJobType
    $numOpenings: Int
    $clientOrganizationId: ID
    $employerContactName: String
    $employerContactEmail: String
    $employerContactPhone: String
    $agencyContactName: String
    $agencyContactEmail: String
    $agencyContactPhone: String
    $location: String
    $duration: String
    $startDate: Time
    $isAsap: Boolean!
    $requirementsDescription: String
    $responsibilitiesDescription: String
    $otherDescription: String
    $vesselId: ID
    $hiredInstructions: String
    $siteAddress: String
    $candidateRoleIds: [ID!]
    $requiredSkills: [ID!]
    $requiredCertifications: [ID!]
    $candidateWorkLocationIds: [ID!]
    $payRateValue: Float
    $payFrequency: MoblyzeJobPayFrequency
    $payCurrency: MoblyzeJobPayCurrency
    $ownerUserId: ID
    $generationRunId: ID
  ) {
    createJob(
      input: {
        bullhornId: $bullhornId
        category: $category
        title: $title
        employmentType: $employmentType
        numOpenings: $numOpenings
        clientOrganizationId: $clientOrganizationId
        employerContactName: $employerContactName
        employerContactEmail: $employerContactEmail
        employerContactPhone: $employerContactPhone
        agencyContactName: $agencyContactName
        agencyContactEmail: $agencyContactEmail
        agencyContactPhone: $agencyContactPhone
        location: $location
        duration: $duration
        startDate: $startDate
        isAsap: $isAsap
        requirementsDescription: $requirementsDescription
        responsibilitiesDescription: $responsibilitiesDescription
        otherDescription: $otherDescription
        vesselId: $vesselId
        hiredInstructions: $hiredInstructions
        siteAddress: $siteAddress
        candidateRoleIds: $candidateRoleIds
        requiredSkills: $requiredSkills
        requiredCertifications: $requiredCertifications
        candidateWorkLocationIds: $candidateWorkLocationIds
        payRateValue: $payRateValue
        payFrequency: $payFrequency
        payCurrency: $payCurrency
        ownerUserId: $ownerUserId
      }
      generationRunId: $generationRunId
    ) {
      id
      criteriaExtractionStatus
    }
  }
`;

// Everything the review/preview page needs to hydrate a draft from an
// existing job. Deliberately narrower than MOBLYZE_JOB_FRAGMENT.
export const JOB_PREVIEW_QUERY = gql`
  query JobPreview($id: ID!) {
    moblyzeJob(id: $id) {
      id
      bullhornId
      status
      category
      title
      employmentType
      numOpenings
      clientOrganizationId
      employerContactName
      employerContactEmail
      employerContactPhone
      agencyContactName
      agencyContactEmail
      agencyContactPhone
      location
      duration
      startDate
      isAsap
      requirementsDescription
      responsibilitiesDescription
      otherDescription
      coverImageUrl
      vesselId
      vessel {
        id
        name
      }
      roles {
        id
        name
      }
      workLocations {
        id
        name
      }
      payRateValue
      payFrequency
      payCurrency
      owner {
        id
        firstName
        lastName
        email
        role
      }
    }
  }
`;

export const UPLOAD_JOB_COVER_IMAGE_MUTATION = gql`
  mutation UploadJobCoverImage($moblyzeJobId: ID!, $imageFile: Upload!) {
    uploadJobCoverImage(moblyzeJobId: $moblyzeJobId, imageFile: $imageFile) {
      id
    }
  }
`;

export const UPDATE_JOB_MUTATION = gql`
  mutation updateJobDetails(
    $bullhornId: String
    $moblyzeJobId: ID!
    $category: String
    $title: String
    $employmentType: MoblyzeJobType
    $numOpenings: Int
    $clientOrganizationId: ID
    $employerContactName: String
    $employerContactEmail: String
    $employerContactPhone: String
    $agencyContactName: String
    $agencyContactEmail: String
    $agencyContactPhone: String
    $location: String
    $duration: String
    $startDate: Time
    $isAsap: Boolean!
    $requirementsDescription: String
    $responsibilitiesDescription: String
    $vesselId: ID
    $hiredInstructions: String
    $siteAddress: String
    $candidateRoleIds: [ID!]
    $candidateWorkLocationIds: [ID!]
    $payRateValue: Float
    $payFrequency: MoblyzeJobPayFrequency
    $payCurrency: MoblyzeJobPayCurrency
    $requiredSkills: [ID!]
    $requiredCertifications: [ID!]
    $ownerUserId: ID
  ) {
    updateJobDetails(
      moblyzeJobId: $moblyzeJobId
      input: {
        category: $category
        title: $title
        employmentType: $employmentType
        numOpenings: $numOpenings
        clientOrganizationId: $clientOrganizationId
        employerContactName: $employerContactName
        employerContactEmail: $employerContactEmail
        employerContactPhone: $employerContactPhone
        agencyContactName: $agencyContactName
        agencyContactEmail: $agencyContactEmail
        agencyContactPhone: $agencyContactPhone
        location: $location
        duration: $duration
        startDate: $startDate
        isAsap: $isAsap
        requirementsDescription: $requirementsDescription
        responsibilitiesDescription: $responsibilitiesDescription
        vesselId: $vesselId
        hiredInstructions: $hiredInstructions
        siteAddress: $siteAddress
        candidateRoleIds: $candidateRoleIds
        requiredSkills: $requiredSkills
        requiredCertifications: $requiredCertifications
        candidateWorkLocationIds: $candidateWorkLocationIds
        payRateValue: $payRateValue
        payFrequency: $payFrequency
        payCurrency: $payCurrency
        bullhornId: $bullhornId
        ownerUserId: $ownerUserId
      }
    ) {
      id
    }
  }
`;

export const UPDATE_MOBLYZE_JOB_STATUS_MUTATION = gql`
  ${MOBLYZE_JOB_FRAGMENT}
  mutation UpdateMoblyzeJobStatus($moblyzeJobId: ID!, $status: MoblyzeJobStatus!) {
    updateMoblyzeJobStatus(moblyzeJobId: $moblyzeJobId, status: $status) {
      ...MoblyzeJobFragment
    }
  }
`;

export const IMPORT_BULLHORN_JOBS_MUTATION = gql`
  ${BULLHORN_JOB_FRAGMENT}
  mutation ImportBullhornJobs($csvFile: Upload!) {
    importBullhornJobs(csvFile: $csvFile) {
      ...bullhornJobFragment
    }
  }
`;

export const GET_SKILLS_QUERY = gql`
  query GetSkills($moblyzeJobId: ID!) {
    getSkills(moblyzeJobId: $moblyzeJobId) {
      id
      name
    }
  }
`;

export const GET_CANDIDATE_ROLES_QUERY = gql`
  {
    candidateRoles {
      id
      name
      requiredSkills {
        id
        name
      }
      requiredCertifications {
        id
        name
      }
    }
  }
`;

export const GET_ALL_CANDIDATE_ROLES_AND_SKILLS_QUERY = gql`
  query AllCandidateRolesAndSkills {
    candidateRoles {
      id
      name
    }
    allSkills {
      id
      name
    }
  }
`;